import { useCallback } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import type {
    TBaseInput,
    TGeneratePasswordInput,
    TMutationEmployeeLoginByAdArgs,
    TMutationEmployeeLoginMutationArgs,
    TMutationGeneratePasswordArgs,
    TMutationUpdateEmployeeNicknameArgs,
    TSessionInputAd
} from '@app/types'
import type { MutationType, QueryType } from '@app/types/types'
import {
    EMPLOYEE_LOGIN,
    EMPLOYEE_LOGIN_WITH_PROVIDER,
    GENERATE_PASSWORD,
    UPDATE_EMPLOYEE_NICKNAME
} from '../mutations/employee'
import { EMPLOYEE_INFO } from '../queries/employee'

export const useEmployee = () => {
    const { data, loading, error } = useQuery<QueryType<'employeeInfo'>>(
        EMPLOYEE_INFO,
        {
            fetchPolicy: 'network-only'
        }
    )

    const employee = data?.employeeInfo ?? undefined

    return { employee, loading, error }
}

export const useEmployeeLogin = () => {
    const [mutate] = useMutation<
        MutationType<'employeeLoginMutation'>,
        TMutationEmployeeLoginMutationArgs
    >(EMPLOYEE_LOGIN)

    return useCallback(
        async (input: TBaseInput) =>
            await mutate({
                variables: { generate: input }
            }),
        [mutate]
    )
}

export const useEmployeeLoginWithProvider = () => {
    const [mutate] = useMutation<
        MutationType<'employeeLoginByAd'>,
        TMutationEmployeeLoginByAdArgs
    >(EMPLOYEE_LOGIN_WITH_PROVIDER)

    return useCallback(
        async (input: TSessionInputAd) =>
            await mutate({
                variables: { input }
            }),
        [mutate]
    )
}

export const useEmployeeRegister = () => {
    const [mutate] = useMutation<unknown, TMutationGeneratePasswordArgs>(
        GENERATE_PASSWORD
    )

    return useCallback(
        async (input: TGeneratePasswordInput) =>
            await mutate({
                variables: { generate: input }
            }),
        [mutate]
    )
}

export const useEmployeeUpdateNickname = () => {
    const [mutate] = useMutation<
        MutationType<'updateEmployeeNickname'>,
        TMutationUpdateEmployeeNicknameArgs
    >(UPDATE_EMPLOYEE_NICKNAME)

    return useCallback(
        async (nickname?: string | null) =>
            await mutate({
                refetchQueries: [{ query: EMPLOYEE_INFO }],
                variables: { data: { nickname } }
            }),
        [mutate]
    )
}
